import { useCallback, useEffect, useLayoutEffect, useRef, useState, type FC, type PointerEvent as ReactPointerEvent } from 'react';
import { LuPlus, LuTrash2, LuRepeat, LuCircleSlash } from 'react-icons/lu';
import Select from './Select';
import { Switch } from './RecordPanel';
import type { AcaoDesfecho, DesfechoModelo, ModeloTarefa } from '../services/ModeloTarefaService';
import type { TipoTarefa } from '../services/TarefaService';
import { nomeEtapa, gruposDoFunil } from '../data/etapas';
import './TrilhoCanvas.css';

/**
 * O quadro do trilho (docs/propostas/tarefas-padrao-quadro.html, direção B).
 *
 * **Sem biblioteca de canvas, de propósito.** O grafo é fechado: seis nós que já
 * existem (as etapas do funil), no máximo ~15 setas, e **não existe criar nó** —
 * o que React Flow traz de valioso (paleta, criação por drop, realce no hover) é
 * justamente o que este produto não pode ter. Sobra arrasto e traçado, que são
 * estas ~300 linhas de `pointer events` + um `<svg>` por cima.
 *
 * As seis caixas estão SEMPRE no quadro: as três que criam tarefa, mais Novos,
 * Ganho e Perdido como alvos. Como não há nada para criar, o gesto que no n8n
 * criaria um nó aqui não tem onde acontecer.
 */

/* O nome da etapa tem um dono só desde que a empresa desenha o funil: `data/etapas`. Este
   arquivo mantinha a própria cópia, com outra grafia e outro nome para a etapa 1. */
export { NOME_ETAPA, nomeEtapa } from '../data/etapas';
export const COR_ETAPA: Record<number, string> = {
    1: 'var(--pl-st-ia)', 2: 'var(--pl-st-qual)', 3: 'var(--pl-st-win)',
    4: 'var(--pl-st-prop)', 5: 'var(--pl-st-neg)', 6: 'var(--pl-st-lost)',
};
/** As etapas que criam tarefa: o miolo do funil da empresa, por maior que ele fique. */
export const etapasComTarefa = () => gruposDoFunil().filter(g => g.plus).map(g => g.alvo);
/** Para onde um desfecho pode levar: o miolo mais os dois desfechos. */
export const destinosDeDesfecho = () => gruposDoFunil().filter(g => g.cor !== 'ia').map(g => g.alvo);
export const LARGURA_NO = 300;
const GRADE = 18;

export type Pos = { x: number; y: number };
export type Layout = Record<string, Pos>;

/* O desfecho no editor carrega uma chave estável: linha nova ainda não tem id do
   banco, e usar o índice faria o React trocar o campo de lugar ao reordenar. */
export type DesfechoEdit = DesfechoModelo & { uid: string };
export type EtapaEdit = Omit<ModeloTarefa, 'desfechos'> & { desfechos: DesfechoEdit[] };
export type Selecao = { tipo: 'no'; statusId: number } | { tipo: 'desfecho'; uid: string } | null;

let contador = 0;
export const novaChave = () => `d${++contador}`;

/** O arranjo com que todo trilho nasce — e para onde o "Organizar" devolve. */
/**
 * Onde cada caixa nasce no quadro. As posições fixas são as do funil que o produto entrega; etapa
 * que a empresa criar entra na mesma faixa do miolo, à direita da última — antes isto era um mapa
 * de seis ids e a caixa dela nascia em cima da primeira, no canto.
 */
const LAYOUT_FIXO: Layout = {
    1: { x: 72, y: 18 },
    2: { x: 72, y: 72 },
    4: { x: 414, y: 72 },
    5: { x: 756, y: 72 },
    3: { x: 954, y: 414 },
    6: { x: 594, y: 504 },
};

export const layoutPadrao = (): Layout => {
    const layout: Layout = { ...LAYOUT_FIXO };

    /* Três por linha: a quarta etapa em diante desce, em vez de nascer fora da janela e dar a
       impressão de que não foi criada. Quem quiser outro desenho arrasta e o layout é salvo. */
    etapasComTarefa().forEach((id, i) => {
        if (layout[id]) return;
        const coluna = i % 3;
        const linha = Math.floor(i / 3);
        layout[id] = { x: 72 + (coluna * 342), y: 72 + (linha * 288) };
    });

    return layout;
};

export const layoutOu = (bruto: string | null | undefined): Layout => {
    if (!bruto) return layoutPadrao();
    try {
        const lido = JSON.parse(bruto) as Layout;
        /* Nó que falta no JSON salvo volta ao padrão: o quadro nunca abre sem caixa. */
        return { ...layoutPadrao(), ...lido };
    } catch {
        return { ...layoutPadrao() };
    }
};

export const acaoDoDestino = (statusDestino: number, statusOrigem: number): AcaoDesfecho => {
    if (statusDestino === statusOrigem) return 'reschedule';
    if (statusDestino === 6) return 'lose';
    return 'move';
};

/**
 * A palavra curta do que o desfecho faz. O DESTINO não entra aqui de propósito:
 * quem o diz é a seta — foi para isso que a coluna "Para" saiu. Escrever o nome
 * da etapa nesta célula comeria 92px da linha e repetiria o desenho.
 */
export const descreverAcao = (d: DesfechoModelo) => {
    if (d.acao === 'move') return 'move';
    if (d.acao === 'lose') return 'perde';
    if (d.acao === 'reschedule') return `+${d.diasReagendamento ?? 1}d`;
    return 'encerra';
};

export const ZOOM_MIN = 0.4;
export const ZOOM_MAX = 1.6;
const limitarZoom = (z: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));

interface Props {
    etapas: EtapaEdit[];
    layout: Layout;
    zoom: number;
    pan: Pos;
    selecao: Selecao;
    tipos: TipoTarefa[];
    erros: Record<string, string>;
    somenteLeitura?: boolean;
    onLayout: (l: Layout) => void;
    onPan: (p: Pos) => void;
    onZoom: (z: number) => void;
    onEtapas: (e: EtapaEdit[]) => void;
    onSelecao: (s: Selecao) => void;
}

const TrilhoCanvas: FC<Props> = ({
    etapas, layout, zoom, pan, selecao, tipos, erros, somenteLeitura = false,
    onLayout, onPan, onZoom, onEtapas, onSelecao,
}) => {
    const mundoRef = useRef<HTMLDivElement>(null);
    const cvRef = useRef<HTMLDivElement>(null);
    /* Medidas do desenho: altura de cada caixa e o centro de cada porta, em
       coordenadas do mundo. Medido do DOM (offsetTop dentro da caixa), não
       calculado a partir do CSS — assim o traço acompanha qualquer mudança de
       altura de campo sem ninguém lembrar de atualizar uma constante. */
    const [alturas, setAlturas] = useState<Record<string, number>>({});
    const [portas, setPortas] = useState<Record<string, Pos>>({});
    const [ligando, setLigando] = useState<{ uid: string; statusId: number; ponta: Pos } | null>(null);

    useLayoutEffect(() => {
        const mundo = mundoRef.current;
        if (!mundo) return;
        const novasAlturas: Record<string, number> = {};
        const novasPortas: Record<string, Pos> = {};
        mundo.querySelectorAll<HTMLElement>('[data-no]').forEach(no => {
            const id = no.dataset.no!;
            novasAlturas[id] = no.offsetHeight;
            no.querySelectorAll<HTMLElement>('[data-porta]').forEach(p => {
                const base = layout[id] ?? { x: 0, y: 0 };
                novasPortas[p.dataset.porta!] = {
                    x: base.x + p.offsetLeft + p.offsetWidth / 2,
                    y: base.y + p.offsetTop + p.offsetHeight / 2,
                };
            });
        });
        setAlturas(a => (JSON.stringify(a) === JSON.stringify(novasAlturas) ? a : novasAlturas));
        setPortas(p => (JSON.stringify(p) === JSON.stringify(novasPortas) ? p : novasPortas));
    }, [etapas, layout, tipos, selecao]);

    /* ─── Os gestos que todo canvas tem ───────────────────────────
       Ctrl/⌘ + rolar dá zoom NO PONTO do cursor (é o que trackpad e mouse
       mandam: o pinch do trackpad chega como wheel com ctrlKey); rolar sozinho
       desloca na vertical e Shift + rolar na horizontal. O listener é registrado
       à mão porque precisa de `passive: false` para conter o zoom do navegador —
       o `onWheel` do React entra passivo e o preventDefault não pega. */
    useEffect(() => {
        const cv = cvRef.current;
        if (!cv) return;
        const aoRolar = (e: WheelEvent) => {
            e.preventDefault();
            const r = cv.getBoundingClientRect();
            if (e.ctrlKey || e.metaKey) {
                const px = e.clientX - r.left, py = e.clientY - r.top;
                /* O ponto do mundo sob o cursor não se move: é isso que faz o
                   zoom parecer "aproximar daqui" em vez de pular. */
                const mx = (px - pan.x) / zoom, my = (py - pan.y) / zoom;
                const novo = limitarZoom(zoom * (e.deltaY > 0 ? 0.9 : 1.1));
                onZoom(novo);
                onPan({ x: px - mx * novo, y: py - my * novo });
                return;
            }
            if (e.shiftKey) onPan({ x: pan.x - e.deltaY - e.deltaX, y: pan.y });
            else onPan({ x: pan.x - e.deltaX, y: pan.y - e.deltaY });
        };
        cv.addEventListener('wheel', aoRolar, { passive: false });
        return () => cv.removeEventListener('wheel', aoRolar);
    }, [pan, zoom, onPan, onZoom]);

    /* Esc larga a seleção — o mesmo que clicar no vazio. */
    useEffect(() => {
        if (!selecao) return;
        const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') onSelecao(null); };
        window.addEventListener('keydown', aoTeclar);
        return () => window.removeEventListener('keydown', aoTeclar);
    }, [selecao, onSelecao]);

    /* ─── Arrastar caixa ─── */

    const arrastarNo = (statusId: number) => (e: ReactPointerEvent) => {
        if (somenteLeitura) return;
        const alvo = e.target as HTMLElement;
        if (alvo.closest('input, button, .ds-select, [data-porta]')) return;
        e.preventDefault();
        const inicio = { x: e.clientX, y: e.clientY };
        const origem = layout[statusId] ?? { x: 0, y: 0 };
        /* Sem piso em zero: travar em 0 punha uma parede invisível na esquerda e
           no topo do quadro, e a caixa parava de andar sem nada à vista. O mundo
           tem coordenada negativa; quem enquadra é o pan. */
        const mover = (ev: PointerEvent) => {
            onLayout({
                ...layout,
                [statusId]: {
                    x: origem.x + (ev.clientX - inicio.x) / zoom,
                    y: origem.y + (ev.clientY - inicio.y) / zoom,
                },
            });
        };
        const soltar = (ev: PointerEvent) => {
            const bruta = {
                x: origem.x + (ev.clientX - inicio.x) / zoom,
                y: origem.y + (ev.clientY - inicio.y) / zoom,
            };
            /* Grade de 18px, a mesma do fundo pontilhado: solta sempre alinhado. */
            onLayout({
                ...layout,
                [statusId]: { x: Math.round(bruta.x / GRADE) * GRADE, y: Math.round(bruta.y / GRADE) * GRADE },
            });
            window.removeEventListener('pointermove', mover);
            window.removeEventListener('pointerup', soltar);
        };
        window.addEventListener('pointermove', mover);
        window.addEventListener('pointerup', soltar);
    };

    /* ─── Arrastar o fundo (pan) ─── */

    const arrastarFundo = (e: ReactPointerEvent) => {
        /* Botão do meio arrasta o quadro de qualquer lugar, inclusive por cima de
           uma caixa — é o atalho que quem usa canvas espera. */
        const doMeio = e.button === 1;
        if (!doMeio && (e.target as HTMLElement).closest('[data-no]')) return;
        if (doMeio) e.preventDefault(); else onSelecao(null);
        const inicio = { x: e.clientX, y: e.clientY };
        const origem = { ...pan };
        const mover = (ev: PointerEvent) =>
            onPan({ x: origem.x + (ev.clientX - inicio.x), y: origem.y + (ev.clientY - inicio.y) });
        const soltar = () => {
            window.removeEventListener('pointermove', mover);
            window.removeEventListener('pointerup', soltar);
        };
        window.addEventListener('pointermove', mover);
        window.addEventListener('pointerup', soltar);
    };

    /* ─── Ligar: puxar da porta até uma etapa ─── */

    const paraMundo = useCallback((clientX: number, clientY: number): Pos => {
        const r = mundoRef.current!.getBoundingClientRect();
        return { x: (clientX - r.left) / zoom, y: (clientY - r.top) / zoom };
    }, [zoom]);

    const puxar = (statusId: number, uid: string) => (e: ReactPointerEvent) => {
        if (somenteLeitura) return;
        e.preventDefault();
        e.stopPropagation();
        onSelecao({ tipo: 'desfecho', uid });
        const mover = (ev: PointerEvent) => setLigando({ uid, statusId, ponta: paraMundo(ev.clientX, ev.clientY) });
        const soltar = (ev: PointerEvent) => {
            window.removeEventListener('pointermove', mover);
            window.removeEventListener('pointerup', soltar);
            setLigando(null);
            const sob = document.elementFromPoint(ev.clientX, ev.clientY) as HTMLElement | null;
            const destino = sob?.closest<HTMLElement>('[data-no]')?.dataset.no;
            /* Soltar no vazio, em Novos ou onde não dá: volta ao que era. */
            if (!destino) return;
            const alvo = Number(destino);
            if (!destinosDeDesfecho().includes(alvo)) return;
            aplicarLigacao(statusId, uid, alvo);
        };
        window.addEventListener('pointermove', mover);
        window.addEventListener('pointerup', soltar);
    };

    /** A ação sai de ONDE a seta chegou — é o que faz a coluna "Para" sumir. */
    const aplicarLigacao = (statusId: number, uid: string, destino: number) => {
        const acao = acaoDoDestino(destino, statusId);
        mudarDesfecho(statusId, uid, {
            acao,
            statusDestinoId: acao === 'move' ? destino : null,
            diasReagendamento: acao === 'reschedule' ? 2 : null,
        });
    };

    /* ─── Edição ─── */

    const mudarEtapa = (statusId: number, campos: Partial<EtapaEdit>) =>
        onEtapas(etapas.map(e => (e.statusId === statusId ? { ...e, ...campos } : e)));

    const mudarDesfecho = (statusId: number, uid: string, campos: Partial<DesfechoEdit>) =>
        onEtapas(etapas.map(e => e.statusId !== statusId ? e : {
            ...e, desfechos: e.desfechos.map(d => (d.uid === uid ? { ...d, ...campos } : d)),
        }));

    const adicionarDesfecho = (statusId: number) => {
        const uid = novaChave();
        onEtapas(etapas.map(e => e.statusId !== statusId ? e : {
            ...e,
            desfechos: [...e.desfechos, {
                uid, rotulo: '', ordem: e.desfechos.length,
                acao: 'move', statusDestinoId: destinosDeDesfecho().find(d => d !== statusId) ?? 3,
            }],
        }));
        onSelecao({ tipo: 'desfecho', uid });
    };

    const removerDesfecho = (statusId: number, uid: string) =>
        onEtapas(etapas.map(e => e.statusId !== statusId ? e : {
            ...e, desfechos: e.desfechos.filter(d => d.uid !== uid),
        }));

    const ligarEtapa = (statusId: number, ligado: boolean) =>
        onEtapas(etapas.map(e => {
            if (e.statusId !== statusId) return e;
            if (!ligado) return { ...e, ativo: false };
            return {
                ...e, ativo: true,
                desfechos: e.desfechos.length > 0 ? e.desfechos : [{
                    uid: novaChave(), rotulo: '', ordem: 0,
                    acao: 'move' as AcaoDesfecho, statusDestinoId: destinosDeDesfecho().find(d => d !== statusId) ?? 3,
                }],
            };
        }));

    /* ─── As setas ─── */

    const caixa = (statusId: number) => {
        const p = layout[statusId] ?? { x: 0, y: 0 };
        const h = alturas[statusId] ?? 40;
        const l = etapasComTarefa().includes(statusId) ? LARGURA_NO : (statusId === 1 ? 92 : 150);
        return { ...p, w: l, h, cx: p.x + l / 2, cy: p.y + h / 2 };
    };

    /** Curva da porta até a borda mais próxima do destino. */
    const caminho = (de: Pos, statusDestino: number) => {
        const c = caixa(statusDestino);
        const entraPorCima = c.y > de.y + 40;
        const alvo = entraPorCima ? { x: c.cx, y: c.y } : { x: c.x, y: Math.min(Math.max(de.y, c.y + 12), c.y + c.h - 12) };
        const dx = Math.max(40, Math.abs(alvo.x - de.x) / 2);
        return entraPorCima
            ? `M ${de.x} ${de.y} C ${de.x + dx} ${de.y}, ${alvo.x} ${alvo.y - 60}, ${alvo.x} ${alvo.y}`
            : `M ${de.x} ${de.y} C ${de.x + dx} ${de.y}, ${alvo.x - dx} ${alvo.y}, ${alvo.x} ${alvo.y}`;
    };

    /** Laço do reagendamento: sai da porta, desce e volta na própria caixa. */
    const laco = (de: Pos, statusId: number) => {
        const c = caixa(statusId);
        const fundo = c.y + c.h + 26;
        return `M ${de.x} ${de.y} C ${de.x + 40} ${de.y}, ${de.x + 40} ${fundo}, ${c.cx} ${fundo} C ${c.x - 40} ${fundo}, ${c.x - 40} ${de.y + 20}, ${c.x} ${de.y + 20}`;
    };

    const setas: { d: string; classe: string; uid: string }[] = [];
    for (const etapa of etapas) {
        if (!etapa.ativo) continue;
        for (const d of etapa.desfechos) {
            const de = portas[d.uid];
            if (!de) continue;
            if (d.acao === 'move' && d.statusDestinoId) setas.push({ d: caminho(de, d.statusDestinoId), classe: 'is-move', uid: d.uid });
            else if (d.acao === 'lose') setas.push({ d: caminho(de, 6), classe: 'is-perde', uid: d.uid });
            else if (d.acao === 'reschedule') setas.push({ d: laco(de, etapa.statusId), classe: 'is-reag', uid: d.uid });
            else setas.push({ d: `M ${de.x} ${de.y} h 34`, classe: 'is-fim', uid: d.uid });
        }
    }

    /* A entrada: de Novos para a primeira etapa com tarefa. */
    const primeira = etapas.find(e => e.ativo);
    const entrada = primeira ? (() => {
        const a = caixa(1), b = caixa(primeira.statusId);
        return `M ${a.cx} ${a.y + a.h} C ${a.cx} ${a.y + a.h + 18}, ${b.cx} ${b.y - 18}, ${b.cx} ${b.y}`;
    })() : null;

    /* O desenho vale de min a max, e o mínimo pode ser NEGATIVO — a caixa anda
       para a esquerda e para cima à vontade. O `<svg>` é posicionado no canto
       mínimo e recebe um viewBox que começa lá, então os caminhos continuam
       escritos em coordenada de mundo, sem conta nenhuma. */
    const cantos = Object.entries(layout);
    const minX = Math.min(0, ...cantos.map(([, p]) => p.x)) - 80;
    const minY = Math.min(0, ...cantos.map(([, p]) => p.y)) - 80;
    const maxX = Math.max(1120, ...cantos.map(([, p]) => p.x + LARGURA_NO)) + 80;
    const maxY = Math.max(560, ...cantos.map(([id, p]) => p.y + (alturas[id] ?? 60))) + 120;
    const mundoW = maxX - minX;
    const mundoH = maxY - minY;

    const opcoesTipo = tipos.map(t => ({ value: String(t.tipoTarefaId), label: t.nome }));

    return (
        <div ref={cvRef} className="tq-cv" onPointerDown={arrastarFundo} onAuxClick={e => e.preventDefault()}>
            <div
                ref={mundoRef}
                className="tq-mundo"
                style={{
                    width: maxX, height: maxY,
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                }}
            >
                <svg
                    className="tq-setas"
                    style={{ left: minX, top: minY }}
                    width={mundoW} height={mundoH}
                    viewBox={`${minX} ${minY} ${mundoW} ${mundoH}`}
                    aria-hidden="true"
                >
                    {entrada && <path className="tq-seta is-entrada" d={entrada} />}
                    {setas.map(s => (
                        <path
                            key={s.uid}
                            className={`tq-seta ${s.classe} ${selecao?.tipo === 'desfecho' && selecao.uid === s.uid ? 'is-sel' : ''}`}
                            d={s.d}
                        />
                    ))}
                    {ligando && portas[ligando.uid] && (
                        <path className="tq-seta is-ligando"
                            d={`M ${portas[ligando.uid].x} ${portas[ligando.uid].y} L ${ligando.ponta.x} ${ligando.ponta.y}`} />
                    )}
                </svg>

                {/* Entrada do funil: existe para dizer onde o trilho começa. */}
                <div className="tq-entr" data-no="1" style={{ left: layout[1]?.x, top: layout[1]?.y }}
                    onPointerDown={arrastarNo(1)}>
                    <i className="tq-dot" style={{ backgroundColor: COR_ETAPA[1] }} />
                    <span>{nomeEtapa(1)}</span>
                </div>

                {etapas.map(etapa => {
                    const pos = layout[etapa.statusId] ?? { x: 0, y: 0 };
                    const sel = selecao?.tipo === 'no' && selecao.statusId === etapa.statusId;
                    return (
                        <section
                            key={etapa.statusId}
                            data-no={etapa.statusId}
                            className={`tq-no ${etapa.ativo ? '' : 'is-off'} ${sel ? 'is-sel' : ''}`}
                            style={{ left: pos.x, top: pos.y, width: LARGURA_NO }}
                            onPointerDown={arrastarNo(etapa.statusId)}
                            onClick={() => onSelecao({ tipo: 'no', statusId: etapa.statusId })}
                        >
                            <header className="tq-no-h">
                                <i className="tq-dot" style={{ backgroundColor: COR_ETAPA[etapa.statusId] }} />
                                <span className="tq-no-nm">{nomeEtapa(etapa.statusId)}</span>
                                <span className="tq-no-sw">
                                    <Switch
                                        checked={etapa.ativo}
                                        disabled={somenteLeitura}
                                        onChange={v => ligarEtapa(etapa.statusId, v)}
                                        label={`Criar tarefa padrão em ${nomeEtapa(etapa.statusId)}`}
                                    />
                                </span>
                            </header>

                            {etapa.ativo ? (
                                <div className="tq-no-b">
                                    <input
                                        className={`tq-inp ${erros[`t-${etapa.statusId}`] ? 'has-error' : ''}`}
                                        type="text" maxLength={120} value={etapa.titulo}
                                        placeholder="O que fazer nesta etapa"
                                        disabled={somenteLeitura}
                                        aria-label={`Tarefa de ${nomeEtapa(etapa.statusId)}`}
                                        onChange={e => mudarEtapa(etapa.statusId, { titulo: e.target.value })}
                                    />
                                    <div className="tq-linha">
                                        <Select
                                            size="sm"
                                            options={opcoesTipo}
                                            value={etapa.tipoTarefaId ? String(etapa.tipoTarefaId) : ''}
                                            onChange={v => mudarEtapa(etapa.statusId, { tipoTarefaId: v ? Number(v) : null })}
                                            placeholder="Tipo"
                                            emptyText="Nenhum tipo cadastrado"
                                            disabled={somenteLeitura}
                                            hasError={!!erros[`tt-${etapa.statusId}`]}
                                            ariaLabel={`Tipo da tarefa em ${nomeEtapa(etapa.statusId)}`}
                                        />
                                        <span className="tq-prazo">
                                            <input
                                                className={`tq-inp ${erros[`p-${etapa.statusId}`] ? 'has-error' : ''}`}
                                                type="number" min={0} max={60} value={etapa.prazoDiasUteis}
                                                disabled={somenteLeitura}
                                                aria-label={`Prazo em dias úteis de ${nomeEtapa(etapa.statusId)}`}
                                                onChange={e => mudarEtapa(etapa.statusId, { prazoDiasUteis: Number(e.target.value) })}
                                            />
                                            <span className="tq-suf">dias úteis</span>
                                        </span>
                                    </div>

                                    <div className="tq-sec">Desfechos</div>
                                    {etapa.desfechos.map(d => {
                                        const selD = selecao?.tipo === 'desfecho' && selecao.uid === d.uid;
                                        const erroD = erros[`r-${d.uid}`] ?? erros[`c-${d.uid}`];
                                        return (
                                            <div key={d.uid} className={`tq-des-wrap ${selD ? 'is-sel' : ''}`}>
                                                {/* `stopPropagation` porque o clique sobe para a caixa, que
                                                    seleciona a si mesma e apagava a seleção do desfecho. */}
                                                <div className="tq-des" onClick={e => { e.stopPropagation(); onSelecao({ tipo: 'desfecho', uid: d.uid }); }}>
                                                    <input
                                                        className={`tq-inp is-fino ${erros[`r-${d.uid}`] ? 'has-error' : ''}`}
                                                        type="text" maxLength={80} value={d.rotulo}
                                                        placeholder="O que aconteceu"
                                                        disabled={somenteLeitura}
                                                        aria-label="Rótulo do desfecho"
                                                        onChange={e => mudarDesfecho(etapa.statusId, d.uid, { rotulo: e.target.value })}
                                                    />
                                                    <span className={`tq-acao is-${d.acao}`}>{descreverAcao(d)}</span>
                                                    <span
                                                        className={`tq-porta is-${d.acao}`}
                                                        data-porta={d.uid}
                                                        title="Puxar para a etapa de destino"
                                                        aria-label="Puxar para a etapa de destino"
                                                        onPointerDown={puxar(etapa.statusId, d.uid)}
                                                    />
                                                </div>

                                                {selD && !somenteLeitura && (
                                                    <div className="tq-des-det">
                                                        <Select
                                                            size="pill"
                                                            options={[
                                                                { value: 'move', label: 'Mover para etapa' },
                                                                { value: 'reschedule', label: 'Reagendar' },
                                                                { value: 'lose', label: 'Marcar como perdida' },
                                                                { value: 'close', label: 'Encerrar' },
                                                            ]}
                                                            value={d.acao}
                                                            onChange={v => {
                                                                const acao = v as AcaoDesfecho;
                                                                mudarDesfecho(etapa.statusId, d.uid, {
                                                                    acao,
                                                                    statusDestinoId: acao === 'move' ? (d.statusDestinoId ?? destinosDeDesfecho().find(x => x !== etapa.statusId) ?? 3) : null,
                                                                    diasReagendamento: acao === 'reschedule' ? (d.diasReagendamento ?? 2) : null,
                                                                });
                                                            }}
                                                            ariaLabel="Ação do desfecho"
                                                        />
                                                        {d.acao === 'move' && (
                                                            <Select
                                                                size="pill"
                                                                options={destinosDeDesfecho().filter(s => s !== etapa.statusId).map(s => ({
                                                                    value: String(s), label: nomeEtapa(s), dot: COR_ETAPA[s],
                                                                }))}
                                                                value={d.statusDestinoId ? String(d.statusDestinoId) : ''}
                                                                onChange={v => mudarDesfecho(etapa.statusId, d.uid, { statusDestinoId: Number(v) })}
                                                                placeholder="Etapa"
                                                                showOptionAccessory
                                                                hasError={!!erros[`c-${d.uid}`]}
                                                                ariaLabel="Etapa de destino"
                                                            />
                                                        )}
                                                        {d.acao === 'reschedule' && (
                                                            <span className="tq-prazo">
                                                                <input
                                                                    className={`tq-inp is-fino ${erros[`c-${d.uid}`] ? 'has-error' : ''}`}
                                                                    type="number" min={1} max={60}
                                                                    value={d.diasReagendamento ?? 2}
                                                                    aria-label="Dias úteis do reagendamento"
                                                                    onChange={e => mudarDesfecho(etapa.statusId, d.uid, { diasReagendamento: Number(e.target.value) })}
                                                                />
                                                                <span className="tq-suf">dias</span>
                                                            </span>
                                                        )}
                                                        <button type="button" className="tq-rem" title="Remover desfecho"
                                                            aria-label={`Remover o desfecho ${d.rotulo || 'sem rótulo'}`}
                                                            onClick={() => removerDesfecho(etapa.statusId, d.uid)}>
                                                            <LuTrash2 size={13} />
                                                        </button>
                                                    </div>
                                                )}
                                                {erroD && <span className="tq-erro">{erroD}</span>}
                                            </div>
                                        );
                                    })}

                                    {etapa.desfechos.length === 0 && (
                                        <p className={`tq-sem ${erros[`d-${etapa.statusId}`] ? 'has-error' : ''}`}>
                                            Sem desfecho, o vendedor não tem o que registrar aqui.
                                        </p>
                                    )}

                                    {!somenteLeitura && (
                                        <button type="button" className="tq-add" onClick={() => adicionarDesfecho(etapa.statusId)}>
                                            <LuPlus size={12} /> Desfecho
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="tq-no-off">sem tarefa padrão nesta etapa</div>
                            )}
                        </section>
                    );
                })}

                {/* Alvos: recebem seta, não criam tarefa. */}
                {[3, 6].map(s => (
                    <div key={s} className={`tq-term is-${s === 3 ? 'ganho' : 'perdido'}`} data-no={s}
                        style={{ left: layout[s]?.x, top: layout[s]?.y }} onPointerDown={arrastarNo(s)}>
                        <i className="tq-dot" style={{ backgroundColor: COR_ETAPA[s] }} />
                        <span>{nomeEtapa(s)}</span>
                        {s === 6 && <LuCircleSlash size={13} className="tq-term-ic" />}
                        {s === 3 && <LuRepeat size={13} className="tq-term-ic tq-oculto" />}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TrilhoCanvas;
